import type { StaffAnalyticsEventRow } from "@lafayette/schemas";

import { stepSentence } from "../event-sentences";

// The pure parts of the Events tab (CHT-2030): how a time, a property line and a search are written. No I/O, no React.

type Value = string | number | boolean;
type Properties = Readonly<Record<string, Value>>;

/** The five keys that the server adds to every event. They are not the event's own properties, so the table row leaves them out. */
const CONTEXT_KEYS: readonly string[] = [
  "taxonomy_version",
  "environment",
  "surface",
  "app_version",
  "build_id",
];

/** The own properties of an event, in the order that they came, without the context keys. */
export function ownProperties(properties: Properties): readonly (readonly [string, Value])[] {
  return Object.entries(properties).filter(([key]) => !CONTEXT_KEYS.includes(key));
}

/** `entry: notification · screen: trips`. The table cell cuts it to one line with CSS. A flag is `yes` or `no`. */
export function propertyLine(properties: Properties): string {
  const own = ownProperties(properties);
  if (own.length === 0) return "no properties";
  return own.map(([key, value]) => `${key}: ${valueText(value)}`).join(" · ");
}

export function valueText(value: Value): string {
  if (typeof value === "boolean") return value ? "yes" : "no";
  return String(value);
}

const dayKey = (date: Date, tz: string) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

function clock(date: Date, tz: string, seconds: boolean): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" } : {}),
    hour12: false,
  }).format(date);
}

/**
 * The time of an event in the zone of the page: `Today 09:52`, `Yesterday 21:14`, or `26 Oct 18:40`. `nowIso` is the clock of the server (the
 * gate), so "today" is the same for every viewer. `seconds` adds the second (the detail panel).
 */
export function eventTime(iso: string, tz: string, nowIso: string, seconds = false): string {
  const date = new Date(iso);
  const now = new Date(nowIso);
  const time = clock(date, tz, seconds);
  const day = dayKey(date, tz);
  if (day === dayKey(now, tz)) return `Today ${time}`;
  if (day === dayKey(new Date(now.getTime() - 86_400_000), tz)) return `Yesterday ${time}`;
  const label = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    day: "numeric",
    month: "short",
  }).format(date);
  return `${label} ${time}`;
}

/** `09:46`, the clock part only (the timeline of a visit). */
export function clockText(iso: string, tz: string): string {
  return clock(new Date(iso), tz, false);
}

/** `Today, 09:12 – 09:52` for a visit. The end is left out when the visit has none. */
export function visitSpan(
  startIso: string,
  endIso: string | null,
  tz: string,
  nowIso: string,
): string {
  const start = eventTime(startIso, tz, nowIso).replace(/ (\d{2}:\d{2})$/u, ", $1");
  return endIso === null ? start : `${start} – ${clockText(endIso, tz)}`;
}

/** Does an event row match a search? It looks at the event name, the plain words and every property key and value that is on screen. */
export function matchesSearch(row: StaffAnalyticsEventRow, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === "") return true;
  const haystack = [
    row.event,
    stepSentence(row.event, row.properties),
    ...Object.entries(row.properties).flatMap(([key, value]) => [key, valueText(value)]),
  ]
    .join("\n")
    .toLowerCase();
  return haystack.includes(needle);
}

export const AREA_LABELS = {
  app: "App",
  requests: "Requests",
  payments: "Payments",
  conversations: "Conversations",
  notifications: "Notifications",
  trips: "Trips",
} as const;

/** The order of the chips in the mock-up (not the order of the domain list). */
export const AREA_ORDER = [
  "app",
  "requests",
  "payments",
  "conversations",
  "notifications",
  "trips",
] as const;

/** A window in words: `26 Oct 18:40 – 27 Oct 09:10`, in the zone of the page. */
export function windowText(window: Readonly<{ from: string; to: string }>, tz: string): string {
  const part = (iso: string) => {
    const date = new Date(iso);
    const day = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      day: "numeric",
      month: "short",
    }).format(date);
    return `${day} ${clockText(iso, tz)}`;
  };
  return `${part(window.from)} – ${part(window.to)}`;
}
